export type UiFrameworkMode = "classic" | "mockup_v2";

const UI_FRAMEWORK_STORAGE_KEY = "umlauf-ui-framework";
const UI_FRAMEWORK_QUERY_KEY = "uiFramework";
const UI_FRAMEWORK_V2_CLASS = "framework-v2";

const normalizeMode = (value: string | null | undefined): UiFrameworkMode | null => {
  const normalized = String(value ?? "")
    .trim()
    .toLowerCase();
  if (!normalized) return null;
  if (normalized === "classic" || normalized === "v1" || normalized === "legacy") return "classic";
  if (normalized === "mockup_v2" || normalized === "v2" || normalized === "mockup" || normalized === "neu") {
    return "mockup_v2";
  }
  return null;
};

const readModeFromQuery = (): UiFrameworkMode | null => {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  return normalizeMode(params.get(UI_FRAMEWORK_QUERY_KEY));
};

const readModeFromStorage = (): UiFrameworkMode | null => {
  if (typeof window === "undefined") return null;
  try {
    return normalizeMode(window.localStorage.getItem(UI_FRAMEWORK_STORAGE_KEY));
  } catch {
    return null;
  }
};

const persistMode = (mode: UiFrameworkMode) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(UI_FRAMEWORK_STORAGE_KEY, mode);
  } catch {
    // ignore persistence errors in private mode or restricted contexts
  }
};

const readModeFromEnv = (): UiFrameworkMode => {
  const envValue = (import.meta as { env?: Record<string, string | undefined> }).env?.VITE_UI_FRAMEWORK;
  return normalizeMode(envValue) ?? "classic";
};

export const resolveUiFrameworkMode = (): UiFrameworkMode => {
  const queryMode = readModeFromQuery();
  if (queryMode) {
    persistMode(queryMode);
    return queryMode;
  }
  return readModeFromStorage() ?? readModeFromEnv();
};

export const applyUiFrameworkMode = (mode: UiFrameworkMode): UiFrameworkMode => {
  const isV2 = mode === "mockup_v2";
  if (typeof document !== "undefined") {
    document.documentElement.classList.toggle(UI_FRAMEWORK_V2_CLASS, isV2);
    document.body.classList.toggle(UI_FRAMEWORK_V2_CLASS, isV2);
  }
  persistMode(mode);
  return mode;
};

export const initUiFrameworkMode = (): UiFrameworkMode => applyUiFrameworkMode(resolveUiFrameworkMode());
